export function normalizeWaitlistStaffName(value) {
  const raw = String(value || "").trim();
  if (!raw || /^no preference$/i.test(raw) || /^any( staff)?$/i.test(raw)) {
    return "Any staff";
  }
  return raw
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function parseDurationMinutes(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.round(value));
  }
  const raw = String(value).trim();
  if (!raw) return 0;
  if (raw.includes(":")) {
    const [hours, minutes] = raw.split(":");
    return Math.max(0, (Number(hours) || 0) * 60 + (Number(minutes) || 0));
  }
  const asNumber = Number(raw);
  return Number.isFinite(asNumber) ? Math.max(0, Math.round(asNumber)) : 0;
}

function minutesLabel(value) {
  const minutes = parseDurationMinutes(value);
  if (minutes <= 0) return "";
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (hours === 0) return `${remaining} min`;
  if (remaining === 0) return `${hours} hr`;
  return `${hours} hr ${remaining} min`;
}

export function waitlistServiceDisplayLines(row) {
  const services = Array.isArray(row?.services) ? row.services : [];
  if (services.length > 0) {
    return services.map((line) => {
      const name = String(line?.name || line?.service_name || "").trim() || "Service";
      const staffName = normalizeWaitlistStaffName(line?.staff_name);
      const durationMinutes = parseDurationMinutes(
        line?.duration_minutes ?? line?.durationMinutes ?? line?.duration
      );
      const durationLabel = String(line?.duration_label || "").trim() || minutesLabel(durationMinutes);
      return {
        name,
        staff_name: staffName,
        duration_minutes: durationMinutes,
        duration_label: durationLabel,
        label: `${name} with ${staffName}`,
      };
    });
  }

  const combined = String(row?.service_name || row?.serviceName || "").trim();
  if (!combined) return [];

  const parts = combined.split(" + ").map((part) => part.trim()).filter(Boolean);
  const staffName = normalizeWaitlistStaffName(row?.staff_name);
  const names = parts.length > 0 ? parts : [combined];

  return names.map((name, index) => ({
    name,
    staff_name: staffName,
    duration_label: index === 0 ? String(row?.duration_label || "").trim() : "",
    label: `${name} with ${staffName}`,
  }));
}

export function waitlistStaffSummary(row) {
  const named = [
    ...new Set(
      waitlistServiceDisplayLines(row)
        .map((line) => line.staff_name)
        .filter((name) => name && name !== "Any staff")
    ),
  ];
  if (named.length === 0) return "Any staff";
  return named.join(", ");
}

function isMonthScopedWaitlistRow(row) {
  const scope = String(row?.preferred_scope || "").toLowerCase();
  const preferredTime = String(row?.preferred_time || "").toLowerCase();
  return scope === "month" || preferredTime === "month";
}

/** Display labels for My Waitlist; prefer API labels, with month-scope fallback. */
export function waitlistPreferredTimeDisplay(row) {
  const apiLabel = String(row?.preferred_time_label || "").trim();
  if (apiLabel) return apiLabel;
  if (!isMonthScopedWaitlistRow(row)) return "Any time";

  const monthSource = row?.preferred_month || row?.preferred_date || row?.date_label;
  if (monthSource) {
    try {
      const date = new Date(`${String(monthSource).slice(0, 7)}-01T12:00:00`);
      if (!Number.isNaN(date.getTime())) {
        const monthName = date.toLocaleString("en-US", { month: "long" });
        return `Anytime During ${monthName}`;
      }
    } catch {
      // fall through
    }
  }
  return "Anytime During the month";
}

export function waitlistDateDisplay(row) {
  const apiLabel = String(row?.date_label || "").trim();
  if (apiLabel) return apiLabel;
  if (!isMonthScopedWaitlistRow(row)) return "";

  const monthSource = row?.preferred_month || row?.preferred_date;
  if (!monthSource) return "";
  try {
    const date = new Date(`${String(monthSource).slice(0, 7)}-01T12:00:00`);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString("en-US", { month: "long", year: "numeric" });
    }
  } catch {
    // fall through
  }
  return "";
}
